/* Shared algo service helpers that load algo definitions, map OE order state to gateway payloads, and validate modify requests. */
import {
  AlgoTradeRequest,
  type RuntimeAlgoRunRequest,
  AlgoType,
  AlgoParameterDataType,
  AlgoDefinition,
  AlgoParameterDefinition,
  OrderSide,
  SingleAlgoFullReport,
  AlgoTypeString,
  AlgoStatus,
  AlgoParams,
  ManualOrderParams,
  OrderRecord,
  SpreadSymbol,
  AlgoParameterItem,
  Symbol, Direction, SecurityTypes,
  OrderEntryOrder,
  OrderType,
} from '@qep/types/src/index';
import moment from 'moment';
import {
  getAquaOrderType,
  normalizeAquaParameterId,
  normalizeAquaParameterValue,
  validateAquaParameters,
} from '../utils/jpm-aqua';
import {Time, Duration, convertHHMMToSeconds} from '../utils/utils';
import {classifyAlgoConstraintRoute} from '../utils/MultiLegLimitCalculator';
import {getRequiredLimitPriceTickError} from './price-tick-validation';

export enum ParameterType {
  StartTime = 'START_TIME',
  EndTime = 'END_TIME',
  Integer = 'INTEGER',
  PickList = 'PICK_LIST',
  LimitPrice = 'LIMIT_PRICE',
}

export enum OrderTypes {
  Manual = 'Manual',
  Algo = 'Algo',
  Spread = 'Synthetic Trader',
}

type LaunchAlgoParams = {
  symbols?: Array<Symbol | SpreadSymbol>;
} & OrderEntryOrder;

const LIMIT_PRICE_KEYS = new Set(['LIMIT_PRICE', 'limitPrice', 'limit_price', 'LIMITPRICE']);
const isLimitPriceParameterId = (parameterId: string): boolean => LIMIT_PRICE_KEYS.has(parameterId) || parameterId.includes('LIMIT_PRICE');
const MULTILEG_TARGET_IDENTITY_PARAMETER_ID = 'MULTILEG_TARGET_QEP_SYMBOL_IDENTITY';
const canonicalLimitPriceParameterId = 'LIMIT_PRICE';
const canonicalNetTargetParameterId = 'LIMIT_NET_TARGET_VALUE_BASE_CURRENCY';
const startedAlgoStatusNames = new Set(['RUNNING', 'PAUSED']);

/** Recognizes authoritative already-started statuses in both provider and normalized API forms. */
const hasAlgoStarted = (algo: Pick<SingleAlgoFullReport, 'algoStatus'>): boolean =>
  startedAlgoStatusNames.has(String(algo.algoStatus).trim().toUpperCase().replace(/^ALGOSTATUS_/, ''));

/** Identifies JPM broker strategies that must submit complete FIX replacement snapshots. */
const isJpmAlgoType = (algoType: unknown): boolean => {
  const providerIdentity = String(algoType ?? '').toUpperCase().replaceAll(/[^A-Z\d]/g, '');
  return providerIdentity.startsWith('JPM');
};

/** Detects ATE-owned QEP algos without changing provider-owned parameter semantics. */
const isQepAlgoType = (algoType: AlgoDefinition['algoType']): boolean => String(algoType).startsWith('ALGOTYPE_');

/** Canonicalizes enum and provider algo names so modify ownership can be checked without trusting an inferred fallback. */
const normalizeAlgoTypeIdentity = (algoType: string | undefined): string => {
  const normalized = algoType?.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase() ?? '';
  const withoutEnumPrefix = normalized.startsWith('ALGOTYPE')
    ? normalized.slice('ALGOTYPE'.length)
    : normalized;
  return withoutEnumPrefix.replace('LIQUIDITYSEEKING', 'LIQUIDITYSEEKER');
};

/** Confirms a modify supplied a raw identity matching the selected canonical QEP algo. */
const isQepAlgoModifyIdentity = (
  canonicalAlgoType: AlgoDefinition['algoType'],
  rawAlgoType: string | undefined,
): boolean => isQepAlgoType(canonicalAlgoType)
  && rawAlgoType !== undefined
  && rawAlgoType.trim() !== ''
  && normalizeAlgoTypeIdentity(rawAlgoType) === normalizeAlgoTypeIdentity(String(canonicalAlgoType));

/** Normalizes a constraint ID so canonical and provider-prefixed aliases compare consistently. */
const normalizeConstraintParameterId = (parameterId: string): string => parameterId.replaceAll(/[^a-zA-Z\d]/g, '').toUpperCase();

/** Detects canonical and provider-prefixed multileg net-target constraint IDs. */
const isNetTargetConstraintParameterId = (parameterId: string): boolean =>
  normalizeConstraintParameterId(parameterId).endsWith('LIMITNETTARGETVALUEBASECURRENCY');

/** Detects true price constraints while excluding the net-target constraint alias. */
const isPriceConstraintParameterId = (parameterId: string): boolean => {
  const normalized = normalizeConstraintParameterId(parameterId);
  return normalized.endsWith('LIMITPRICE') && !isNetTargetConstraintParameterId(parameterId);
};

type AteConstraintKind = 'price' | 'net-target';

/** Classifies an ATE constraint without stripping its persisted provider-facing identifier. */
const getAteConstraintKind = (parameterId: string): AteConstraintKind | undefined => {
  if (isPriceConstraintParameterId(parameterId)) {
    return 'price';
  }

  return isNetTargetConstraintParameterId(parameterId) ? 'net-target' : undefined;
};

/** Canonicalizes the one ATE constraint only after the submitted child legs select its semantics. */
const canonicalizeAteConstraintParameter = (
  parameters: AlgoParameterItem[],
  order: LaunchAlgoParams,
  algoType: string,
): AlgoParameterItem[] => {
  if (!isQepAlgoType(algoType)) {
    return parameters;
  }

  const constraintParameters = parameters.filter(parameter =>
    isPriceConstraintParameterId(parameter.algoParameterId)
    || isNetTargetConstraintParameterId(parameter.algoParameterId));
  if (order.legs.length <= 1) {
    const constraint = constraintParameters.find(parameter => isPriceConstraintParameterId(parameter.algoParameterId))
      ?? constraintParameters[0];
    return constraint === undefined
      ? parameters
      : [
        ...parameters.filter(parameter => !constraintParameters.includes(parameter)),
        {...constraint, algoParameterId: canonicalLimitPriceParameterId},
      ];
  }

  const route = classifyAlgoConstraintRoute(order.legs);
  if (route.kind === 'invalid') {
    const legNumbers = route.legIndexes.map(index => index + 1).join(', ');
    throw new Error(`Cannot submit multileg algo: contract unit metadata is ${route.reason === 'missing-contract-unit' ? 'missing' : 'unsupported'} for leg(s) ${legNumbers}.`);
  }

  const prefersPrice = route.kind === 'limit-price';
  const constraint = constraintParameters.find(parameter =>
    prefersPrice
      ? isPriceConstraintParameterId(parameter.algoParameterId)
      : isNetTargetConstraintParameterId(parameter.algoParameterId))
    ?? constraintParameters[0];
  if (constraint === undefined) {
    return parameters;
  }

  return [
    ...parameters.filter(parameter => !constraintParameters.includes(parameter)),
    {
      ...constraint,
      algoParameterId: prefersPrice ? canonicalLimitPriceParameterId : canonicalNetTargetParameterId,
    },
  ];
};

/** Canonicalizes only OE2's fallback field name while retaining any persisted ATE constraint ID. */
const normalizeAteModifyConstraintAliases = (parameters: AlgoParameterItem[]): AlgoParameterItem[] =>
  parameters.map(parameter => parameter.algoParameterId === 'limitPrice'
    ? {...parameter, algoParameterId: canonicalLimitPriceParameterId}
    : parameter);

const GENERIC_ALGO_PREFIXES = [AlgoTypeString.ALGOTYPE_SINGLELEG, AlgoTypeString.ALGOTYPE_MULTILEG];
const MARKET_ORDER_TYPE_VALUES = new Set([
  String(OrderType.ORDERTYPE_MARKET),
  'MARKET',
  'ORDERTYPE_MARKET',
  'ORDER_TYPE_MARKET',
  'MKT',
]);
const LIMIT_ORDER_TYPE_VALUES = new Set([
  String(OrderType.ORDERTYPE_LIMIT),
  'LIMIT',
  'ORDERTYPE_LIMIT',
  'ORDER_TYPE_LIMIT',
  'LMT',
]);

/** Identifies run-specific parameters that must be derived from the order being submitted. */
const isRunDerivedAlgoParameterId = (parameterId: string): boolean =>
  parameterId.includes(MULTILEG_TARGET_IDENTITY_PARAMETER_ID);

export const ABORTABLE_ALGO_STATUSES: AlgoStatus[] = [AlgoStatus.ALGOSTATUS_LAUNCH_REQUESTED, AlgoStatus.ALGOSTATUS_RUNNING, AlgoStatus.ALGOSTATUS_PAUSED];

type DefaultManualParams = Omit<ManualOrderParams, 'orderType' | 'timeInForce'> & {
  orderType: string;
  timeInForce: string;
};

type DefaultAlgoParams = Omit<AlgoParams, 'startTime' | 'duration'> & {
  startTime: string | null;
  duration: string | null;
} & Record<string, unknown>;

type DefaultParameters = {
  manualParams: DefaultManualParams;
  algoParams: DefaultAlgoParams;
};

const defaultParameters: DefaultParameters = {
  manualParams: {
    orderType: '2',
    timeInForce: '0',
  },
  algoParams: {
    startTime: null,
    launchImmediately: true,
    duration: null,
  },
};

let algos: AlgoDefinition[] = [];

const normalizeAlgoList = (algoList: AlgoDefinition[] | null | undefined): AlgoDefinition[] => Array.isArray(algoList) ? algoList : [];

export async function loadAlgos() {
  try {
    const algoList = await globalThis.QEP.apiServices.algo.getAlgoDefinition();
    algos = normalizeAlgoList(algoList);
    return algos;
  } catch (error) {
    console.error('Error loading algos:', error);
    algos = [];
    return [];
  }
}

/* @deprecated - unused */
export async function loadAlgoList(orderType?: OrderTypes): Promise<DefaultParameters> {
  await loadAlgos();
  return getDefaultParameters(orderType);
}

export function getAlgoById(algoType: AlgoType | null | string): AlgoDefinition | null {
  const algoList = normalizeAlgoList(algos);
  const algo = algoList.find(algo => algo.algoType === algoType);
  if (!algo) {
    return algoList[0] ?? null;
  }

  return algo;
}

export function getAlgoList(): AlgoDefinition[] | null {
  return normalizeAlgoList(algos);
}

export function getAlgoTypeByNumberOfLegs(numOfLegs: number, definitions?: AlgoDefinition[] | null): AlgoDefinition | undefined {
  const algoList: AlgoDefinition[] | null = definitions ? definitions : getAlgoList();
  const algo = algoList?.find(algo => {
    // There are more algo types to come and hopefully, along with it, a better
    // way to determine what types can automatically be applied to a specific symbol set
    // i.e. algo.minimumLegs <= numOfLegs >= algo.maximumLegs

    // If this function is used after all legs are removed we want to return to a Single Leg algo
    // treating 0 legs as 1 leg will result in Single Leg algo type being returned
    const algoType = numOfLegs <= 1 ? 'single' : 'multi';
    return algo.algoType.toLowerCase().includes(algoType);
  });

  return algo;
}

export function getDefaultParameters(algoType?: AlgoTypeString | OrderTypes | string | null): DefaultParameters {
  const defaults: DefaultParameters = JSON.parse(JSON.stringify({...defaultParameters}));

  if (!algos?.length) {
    return defaults;
  }

  // get defaults
  const activeAlgo = getAlgoById(algoType ?? null);
  if (!activeAlgo) {
    return defaults;
  }

  activeAlgo.parameterDefinitions.map(paramItem => {
    defaults.algoParams[paramItem.algoParameterId] = paramItem.defaultValue;
  });

  if (algoType) {
    defaults.algoParams.algoId = algoType;
  }

  return defaults;
}

export async function getAlgoDetails(id: string): Promise<SingleAlgoFullReport> {
  const details = await globalThis.QEP.apiServices.algo.fetchAlgoDetails(id);
  if (!details) {
    throw new Error(`Algo details unavailable for ${id}.`);
  }

  return details;
}

export async function launchAlgo(order: LaunchAlgoParams, algoToModify?: SingleAlgoFullReport) {
  const algoRunRequest = await orderToAlgoRunRequest(order, algoToModify);
  if (algoToModify) {
    return globalThis.QEP.apiServices.algo.modifyAlgo({algoRunId: algoToModify.algoRunId, ...algoRunRequest});
  }

  return globalThis.QEP.apiServices.algo.runAlgo(algoRunRequest);
}

/** Blocks canonical true Limit Price requests that cannot be verified against the selected product tick. */
function validateAlgoLimitPriceTick(parameters: AlgoParameterItem[], order: LaunchAlgoParams): void {
  const limitPriceParameters = parameters.filter(parameter =>
    isPriceConstraintParameterId(parameter.algoParameterId));
  if (limitPriceParameters.length === 0) {
    return;
  }

  const selectedProduct = order.symbols?.[0] ?? order.legs[0]?.symbol;
  const selectedSecurityType = selectedProduct?.secType ?? order.legs[0]?.securityType;
  if (selectedSecurityType !== SecurityTypes.Future && selectedSecurityType !== SecurityTypes.FuturesSpread) {
    return;
  }

  for (const limitPriceParameter of limitPriceParameters) {
    const error = getRequiredLimitPriceTickError(limitPriceParameter.doubleValue, selectedProduct?.tickSize);
    if (error) {
      throw new Error(error);
    }
  }
}

const getParameterDefinition = (algo: AlgoDefinition, parameterId: string): AlgoParameterDefinition | undefined => {
  const exactMatch = algo.parameterDefinitions.find(def => def.algoParameterId === parameterId);
  if (exactMatch) {
    return exactMatch;
  }

  const algoType = String(algo.algoType);
  const normalizedParameterId = normalizeParameterIdForCompare(parameterId, algoType);
  const normalizedMatch = algo.parameterDefinitions.find(definition => {
    const normalizedDefinitionId = normalizeParameterIdForCompare(definition.algoParameterId, algoType);
    return normalizedDefinitionId === normalizedParameterId
      || definition.algoParameterId.endsWith(`_${normalizedParameterId}`);
  });
  if (normalizedMatch) {
    return normalizedMatch;
  }

  // ATE can report either constraint identity even when the loaded definition exposes only its counterpart.
  // Reuse the counterpart's datatype metadata without changing the identifier supplied by the draft.
  if (isQepAlgoType(algo.algoType)
    && (isPriceConstraintParameterId(parameterId) || isNetTargetConstraintParameterId(parameterId))) {
    return algo.parameterDefinitions.find(definition =>
      isPriceConstraintParameterId(definition.algoParameterId)
      || isNetTargetConstraintParameterId(definition.algoParameterId));
  }

  if (parameterId === 'limitPrice') {
    return algo.parameterDefinitions.find(def => isLimitPriceParameterId(def.algoParameterId));
  }

  return undefined;
};

type AlgoParamValueField =
  | {field: 'stringValue'; value: string | undefined}
  | {field: 'doubleValue'; value: number | undefined}
  | {field: 'intValue'; value: number | undefined};

const isSelectOneDataType = (dataType: unknown): boolean => dataType === AlgoParameterDataType.DATATYPE_SELECT_ONE
  || dataType === 'DATATYPE_SELECT_ONE'
  || dataType === String(AlgoParameterDataType.DATATYPE_SELECT_ONE);

const isNumericDataType = (dataType: unknown): boolean => dataType === AlgoParameterDataType.DATATYPE_NUMERIC_FLOAT
  || dataType === AlgoParameterDataType.DATATYPE_NUMERIC_INT
  || dataType === 'DATATYPE_NUMERIC_FLOAT'
  || dataType === 'DATATYPE_NUMERIC_INT'
  || dataType === String(AlgoParameterDataType.DATATYPE_NUMERIC_FLOAT)
  || dataType === String(AlgoParameterDataType.DATATYPE_NUMERIC_INT);

const getStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return undefined;
};

const isBlankString = (value: unknown): value is string => typeof value === 'string' && value.trim() === '';

const isMissingParameterValue = (value: unknown): boolean => value === undefined || value === null || isBlankString(value);

const getNumberValue = (value: unknown): number | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '-') {
      return undefined;
    }
  }

  const numericValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  // Handle empty string case - Number("") returns 0
  if (isBlankString(value)) {
    return undefined;
  }

  return Number.isNaN(numericValue) ? undefined : numericValue;
};

/**
 * Narrows unknown API values to object records before optional nested reads.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object';
}

/**
 * Converts unknown API values to records while keeping nested reads compact.
 */
function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

const normalizeAlgoParameterDataType = (dataType: unknown): AlgoParameterDataType | undefined => {
  if (typeof dataType === 'number') {
    return dataType as AlgoParameterDataType;
  }

  switch (dataType) {
    case 'DATATYPE_DATE': {
      return AlgoParameterDataType.DATATYPE_DATE;
    }

    case 'DATATYPE_DATETIME': {
      return AlgoParameterDataType.DATATYPE_DATETIME;
    }

    case 'DATATYPE_NUMERIC_INT': {
      return AlgoParameterDataType.DATATYPE_NUMERIC_INT;
    }

    case 'DATATYPE_NUMERIC_FLOAT': {
      return AlgoParameterDataType.DATATYPE_NUMERIC_FLOAT;
    }

    case 'DATATYPE_SELECT_ONE': {
      return AlgoParameterDataType.DATATYPE_SELECT_ONE;
    }

    case 'DATATYPE_SELECT_MUILTIPLE': {
      return AlgoParameterDataType.DATATYPE_SELECT_MUILTIPLE;
    }

    case 'DATATYPE_STRING': {
      return AlgoParameterDataType.DATATYPE_STRING;
    }

    case 'DATATYPE_SECURITY': {
      return AlgoParameterDataType.DATATYPE_SECURITY;
    }

    case 'DATATYPE_PRICE': {
      return AlgoParameterDataType.DATATYPE_PRICE;
    }

    case 'DATATYPE_QUANTITY': {
      return AlgoParameterDataType.DATATYPE_QUANTITY;
    }

    default: {
      return undefined;
    }
  }
};

const isOrderTypeDataType = (dataType: unknown): boolean => dataType === AlgoParameterDataType.DATATYPE_ORDER_TYPE
  || dataType === 'DATATYPE_ORDER_TYPE'
  || dataType === String(AlgoParameterDataType.DATATYPE_ORDER_TYPE);

const normalizeOrderTypeValue = (value: string | number | boolean | undefined): string =>
  typeof value === 'string' || typeof value === 'number'
    ? String(value).trim().replaceAll('-', '_').replaceAll(' ', '_').toUpperCase()
    : '';

const resolveDefaultOrderType = (possibleSelectValues: string[] = []): string => {
  const normalizedValues = possibleSelectValues.map(value => normalizeOrderTypeValue(value));
  if (normalizedValues.some(value => MARKET_ORDER_TYPE_VALUES.has(value))) {
    return String(OrderType.ORDERTYPE_MARKET);
  }

  if (normalizedValues.some(value => LIMIT_ORDER_TYPE_VALUES.has(value))) {
    return String(OrderType.ORDERTYPE_LIMIT);
  }

  return String(OrderType.ORDERTYPE_MARKET);
};

/**
 * Checks order-type values from OE2 state or gateway definitions for limit-order semantics.
 */
const isLimitOrderTypeValue = (value: string | number | boolean | undefined): boolean =>
  LIMIT_ORDER_TYPE_VALUES.has(normalizeOrderTypeValue(value));

const getLimitComparisonValue = (value: unknown): number | string | undefined => {
  if (isMissingParameterValue(value)) {
    return undefined;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? value : numericValue;
  }

  if (typeof value === 'object' && value !== null && 'doubleValue' in value) {
    return getLimitComparisonValue((value as {doubleValue?: unknown}).doubleValue);
  }

  return undefined;
};

/**
 * Returns the primitive value encoded in an algo additional-parameter item.
 */
const getAlgoParameterItemValue = (parameter: {
  doubleValue?: unknown;
  intValue?: unknown;
  stringValue?: unknown;
}): unknown => parameter.doubleValue ?? parameter.intValue ?? parameter.stringValue;

/**
 * Converts API algo parameter shapes into entries that can be compared by key.
 */
const getAlgoParameterEntries = (algoParameters: unknown): Array<[string, unknown]> => {
  if (Array.isArray(algoParameters)) {
    return algoParameters
      .map((parameter: {
        algoParameterId?: string;
        boolValue?: unknown;
        doubleValue?: unknown;
        intValue?: unknown;
        selectedValues?: unknown;
        stringValue?: unknown;
        value?: unknown;
      }): [string, unknown] | undefined => {
        if (!parameter.algoParameterId) {
          return undefined;
        }

        return [
          parameter.algoParameterId,
          parameter.doubleValue ?? parameter.intValue ?? parameter.stringValue ?? parameter.boolValue ?? parameter.selectedValues ?? parameter.value,
        ];
      })
      .filter((entry): entry is [string, unknown] => entry !== undefined);
  }

  if (typeof algoParameters === 'object' && algoParameters !== null) {
    return Object.entries(algoParameters);
  }

  return [];
};

/**
 * Collects request wrappers that can carry the current or original modify algo details.
 */
function getAlgoRequestRecords(algoDetails: Partial<SingleAlgoFullReport>): Array<Record<string, unknown>> {
  const data = algoDetails as Record<string, unknown>;
  const newAlgoRequest = toRecord(data.newAlgoRequest);
  const algoRunStatus = toRecord(data.algoRunStatus);
  const statusNewAlgoRequest = toRecord(algoRunStatus.newAlgoRequest);
  return [
    toRecord(data.algoRunRequest),
    toRecord(data.algoRunModifyRequest),
    toRecord(newAlgoRequest.algoRunRequest),
    toRecord(toRecord(newAlgoRequest.newAlgo).algoRunRequest),
    toRecord(algoRunStatus.algoRunRequest),
    toRecord(algoRunStatus.algoRunModifyRequest),
    toRecord(statusNewAlgoRequest.algoRunRequest),
    toRecord(toRecord(statusNewAlgoRequest.newAlgo).algoRunRequest),
  ].filter(record => Object.keys(record).length > 0);
}

/**
 * Reads loaded algo parameters from both report-style and request-style response shapes.
 */
function getAlgoDetailsParameterEntries(algoDetails: Partial<SingleAlgoFullReport>): Array<[string, unknown]> {
  const data = algoDetails as Record<string, unknown>;
  return [
    ...getAlgoParameterEntries(algoDetails.algoParameters),
    ...getAlgoParameterEntries(data.additionalParameters),
    ...getAlgoRequestRecords(algoDetails).flatMap(record => getAlgoParameterEntries(record.additionalParameters)),
  ];
}

/**
 * Returns the first usable API date string from fetched algo detail fallbacks.
 */
function getFirstApiDateString(values: unknown[]): string | undefined {
  for (const value of values) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString();
    }

    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }

  return undefined;
}

/**
 * Reads the start time from report-style or request-style modify details.
 */
function getAlgoStartDate(algoDetails: Partial<SingleAlgoFullReport>): string | undefined {
  const data = algoDetails as Record<string, unknown>;
  const requestRecords = getAlgoRequestRecords(algoDetails);
  return getFirstApiDateString([
    algoDetails.startDate,
    data.startTimeRequestedUtc,
    ...requestRecords.map(record => record.startTimeRequestedUtc),
    data.startTimeUtc,
    toRecord(data.algoRunStatus).startTimeUtc,
  ]);
}

/**
 * Reads the end time from report-style or request-style modify details.
 */
function getAlgoEndDate(algoDetails: Partial<SingleAlgoFullReport>): string | undefined {
  const data = algoDetails as Record<string, unknown>;
  const requestRecords = getAlgoRequestRecords(algoDetails);
  return getFirstApiDateString([
    algoDetails.endDate,
    data.endTimeRequestedUtc,
    ...requestRecords.map(record => record.endTimeRequestedUtc),
    data.endTimeUtc,
    toRecord(data.algoRunStatus).endTimeUtc,
  ]);
}

/**
 * Reads duration seconds from report-style or request-style modify details.
 */
function getAlgoDurationInSeconds(algoDetails: Partial<SingleAlgoFullReport>): number | undefined {
  const duration = [
    algoDetails.duration,
    ...getAlgoRequestRecords(algoDetails).map(record => record.durationInSeconds),
  ].find(value => value !== undefined && value !== null);
  let numericDuration = Number.NaN;
  if (typeof duration === 'number') {
    numericDuration = duration;
  } else if (typeof duration === 'string') {
    numericDuration = Number(duration);
  }

  return Number.isFinite(numericDuration) ? numericDuration : undefined;
}

/**
 * Removes the algo-type prefix from API parameter IDs so legacy and OE2 keys can be matched.
 */
function normalizeParameterIdForCompare(parameterId: string, algoType: string): string {
  const prefixes = [algoType, ...GENERIC_ALGO_PREFIXES];
  for (const prefix of new Set(prefixes)) {
    const prefixedId = `${prefix}_`;
    if (parameterId.startsWith(prefixedId)) {
      return parameterId.slice(prefixedId.length);
    }
  }

  return parameterId;
}

/**
 * Treats both legacy `LIMIT_PRICE` and OE2 notional-limit IDs as limit price parameters.
 */
const isLimitLikeParameter = (parameterId: string, definition?: AlgoParameterDefinition): boolean => {
  const normalizedId = parameterId.replaceAll('_', '').replaceAll(' ', '').toUpperCase();
  const normalizedDisplayName = definition?.displayName?.replaceAll('_', '').replaceAll(' ', '').toUpperCase() ?? '';

  return isLimitPriceParameterId(parameterId)
    || normalizedId.includes('LIMIT')
    || normalizedDisplayName.includes('LIMIT');
};

/**
 * Uses numeric comparison for prices, quantities, and numeric algo parameters.
 */
const shouldCompareAsNumber = (definition?: AlgoParameterDefinition): boolean => {
  if (!definition) {
    return false;
  }

  const normalizedDataType = normalizeAlgoParameterDataType(definition.dataType);
  return isLimitLikeParameter(definition.algoParameterId, definition)
    || isNumericDataType(definition.dataType)
    || normalizedDataType === AlgoParameterDataType.DATATYPE_PRICE
    || normalizedDataType === AlgoParameterDataType.DATATYPE_QUANTITY;
};

/**
 * Converts values to the comparison shape expected for a specific algo definition.
 */
const toComparableParameterValue = (value: unknown, definition?: AlgoParameterDefinition): number | string | boolean | undefined => {
  const rawValue = typeof value === 'object' && value !== null
    && ('doubleValue' in value || 'intValue' in value || 'stringValue' in value)
    ? getAlgoParameterItemValue(value as {doubleValue?: unknown; intValue?: unknown; stringValue?: unknown})
    : value;

  if (isMissingParameterValue(rawValue)) {
    return undefined;
  }

  if (shouldCompareAsNumber(definition)) {
    return getLimitComparisonValue(rawValue);
  }

  if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
    return rawValue;
  }

  return undefined;
};

/**
 * Finds the original algo parameter value that corresponds to a submitted parameter item.
 */
const findExistingParameterValue = (
  parameter: AlgoParameterItem,
  existingEntries: Array<[string, unknown]>,
  algo: AlgoDefinition,
): unknown => {
  const algoType = String(algo.algoType);
  const parameterDefinition = getParameterDefinition(algo, parameter.algoParameterId);
  const normalizedParameterId = normalizeParameterIdForCompare(parameter.algoParameterId, algoType);
  const exactEntry = existingEntries.find(([key]) =>
    key === parameter.algoParameterId
    || normalizeParameterIdForCompare(key, algoType) === normalizedParameterId
    || key.endsWith(`_${normalizedParameterId}`));

  if (exactEntry) {
    return exactEntry[1];
  }

  if (isLimitLikeParameter(parameter.algoParameterId, parameterDefinition)) {
    return existingEntries.find(([key]) => isLimitLikeParameter(normalizeParameterIdForCompare(key, algoType)))?.[1];
  }

  return undefined;
};

/**
 * Checks whether a submitted param value differs from the loaded algo's current value.
 */
const hasSubmittedParameterChanged = (
  parameter: AlgoParameterItem,
  existingEntries: Array<[string, unknown]>,
  algo: AlgoDefinition,
): boolean => {
  const parameterDefinition = getParameterDefinition(algo, parameter.algoParameterId);
  const submittedValue = toComparableParameterValue(getAlgoParameterItemValue(parameter), parameterDefinition);
  const existingValue = toComparableParameterValue(findExistingParameterValue(parameter, existingEntries, algo), parameterDefinition);

  return submittedValue !== existingValue;
};

/**
 * Detects whether an explicit limit-like algo parameter has already been mapped to the payload.
 */
const hasSubmittedLimitPriceParameter = (
  paramsToSubmit: AlgoParameterItem[],
  algo: AlgoDefinition,
): boolean => paramsToSubmit.some(parameter => {
  const parameterDefinition = getParameterDefinition(algo, parameter.algoParameterId);
  return isLimitLikeParameter(parameter.algoParameterId, parameterDefinition);
});

/**
 * Preserves OE2's fallback limitPrice field when an algo has order-type support but no explicit limit-price definition.
 */
const appendFallbackLimitPriceParameter = (
  paramsToSubmit: AlgoParameterItem[],
  algo: AlgoDefinition,
  orderType: string | undefined,
  order: LaunchAlgoParams,
): void => {
  const fallbackLimitPrice = getNumberValue((order.params as AlgoParams).limitPrice);
  if (
    (orderType === undefined
      ? !isQepAlgoType(algo.algoType)
      : !(isLimitOrderTypeValue(orderType)
        || (algo.algoType === 'JPM_AQUA' && getAquaOrderType(orderType) === '4')))
      || fallbackLimitPrice === undefined
      || hasSubmittedLimitPriceParameter(paramsToSubmit, algo)
  ) {
    return;
  }

  paramsToSubmit.push({
    algoParameterId: 'LIMIT_PRICE',
    doubleValue: fallbackLimitPrice,
  });
};

type DraftParameterMatch = {
  found: boolean;
  value: unknown;
};

/**
 * Finds a draft OE2 param corresponding to a loaded algo parameter key.
 */
const findDraftParameterMatch = (
  existingParameterId: string,
  params: AlgoParams,
  algo: AlgoDefinition,
): DraftParameterMatch => {
  if (Object.hasOwn(params, existingParameterId)) {
    return {found: true, value: params[existingParameterId]};
  }

  const algoType = String(algo.algoType);
  const normalizedExistingId = normalizeParameterIdForCompare(existingParameterId, algoType);
  const directEntry = Object.entries(params).find(([key]) =>
    normalizeParameterIdForCompare(key, algoType) === normalizedExistingId
    || normalizedExistingId.endsWith(`_${normalizeParameterIdForCompare(key, algoType)}`));

  if (directEntry) {
    return {found: true, value: directEntry[1]};
  }

  if (isLimitLikeParameter(normalizedExistingId)) {
    const limitEntry = Object.entries(params).find(([key]) => {
      const definition = getParameterDefinition(algo, key);
      return isLimitLikeParameter(key, definition);
    });

    if (limitEntry) {
      return {found: true, value: limitEntry[1]};
    }
  }

  return {found: false, value: undefined};
};

const returnDataTypeValueField = (algoParamDef: AlgoParameterDefinition, value: unknown): AlgoParamValueField | undefined => {
  if (isLimitPriceParameterId(algoParamDef.algoParameterId)) {
    return {field: 'doubleValue', value: getNumberValue(value)};
  }

  if (isSelectOneDataType(algoParamDef.dataType)) {
    return {field: 'stringValue', value: getStringValue(value)};
  }

  if (isNumericDataType(algoParamDef.dataType)) {
    return {field: 'doubleValue', value: getNumberValue(value)};
  }

  switch (normalizeAlgoParameterDataType(algoParamDef.dataType)) {
    case AlgoParameterDataType.DATATYPE_NUMERIC_FLOAT: {
      return {field: 'doubleValue', value: getNumberValue(value)};
    }

    case AlgoParameterDataType.DATATYPE_PRICE: {
      return {field: 'doubleValue', value: getNumberValue(value)};
    }

    case AlgoParameterDataType.DATATYPE_QUANTITY: {
      const numberValue = getNumberValue(value);
      return {field: 'intValue', value: numberValue === undefined ? undefined : Math.trunc(numberValue)};
    }
  }

  return {field: 'stringValue', value: getStringValue(value)};
};

// Preserves richer option identity on algo legs so gateway lookups can
// deterministically resolve option contracts for limits/FIX assembly.
const buildAlgoTradeSymbolIdentity = (leg: OrderRecord, symbol: Symbol): AlgoTradeRequest['symbolIdentity'] => {
  const strikePrice = getNumberValue(symbol.strikePrice ?? leg.strikePrice);
  const identity: Record<string, unknown> = {
    symbol: symbol.symbol || '',
    segmentMic: symbol.segmentMic || leg.segmentMic || '',
  };

  if (symbol.exchangeSymbol) {
    identity.exchangeSymbol = symbol.exchangeSymbol;
  }

  if (symbol.rootSymbol || leg.root) {
    identity.rootSymbol = symbol.rootSymbol ?? leg.root;
  }

  if (symbol.underlyingRootSymbol || leg.underlyingRootSymbol) {
    identity.underlyingRootSymbol = symbol.underlyingRootSymbol ?? leg.underlyingRootSymbol;
  }

  if (symbol.contractYYYYMM || leg.contractYYYYMM) {
    identity.contractYYYYMM = symbol.contractYYYYMM ?? leg.contractYYYYMM;
  }

  if (strikePrice !== undefined) {
    identity.strikePrice = strikePrice;
  }

  if (symbol.callPut || leg.callPut) {
    identity.callPut = symbol.callPut ?? leg.callPut;
  }

  if (symbol.secType || leg.securityType) {
    identity.secType = (symbol.secType ?? leg.securityType)!;
  }

  return identity as AlgoTradeRequest['symbolIdentity'];
};

/** Builds a run or modify request, validating Aqua timing before serialization and preserving explicit changes. */
async function orderToAlgoRunRequest(
  order: LaunchAlgoParams,
  modifyingAlgo?: SingleAlgoFullReport,
): Promise<RuntimeAlgoRunRequest> {
  const algo: AlgoDefinition | null = getAlgoById((order.params as AlgoParams).algoId);
  if (!algo) {
    throw new Error('No Algo Found');
  }

  const submittedAlgoType = typeof modifyingAlgo?.algoType === 'string' && modifyingAlgo.algoType.trim() !== ''
    ? modifyingAlgo.algoType
    : algo.algoType;
  const isAteAlgo = modifyingAlgo === undefined
    ? isQepAlgoType(algo.algoType)
    : isQepAlgoModifyIdentity(algo.algoType, modifyingAlgo.algoType);

  const isAqua = submittedAlgoType === 'JPM_AQUA';
  if (isAqua) {
    const params = order.params as AlgoParams;
    const parameterError = Object.values(validateAquaParameters(params))[0];
    if (parameterError) {
      throw new Error(parameterError);
    }

    const hasDuration = !isMissingParameterValue(params.duration);
    const hasEndTime = !isMissingParameterValue(params.endTime);
    if (hasDuration === hasEndTime) {
      throw new Error('Aqua requires either Duration or End Time, never both.');
    }

    if (hasDuration && (typeof params.duration !== 'string'
      || !/^\d+:[0-5]\d$/.test(params.duration)
      || params.duration.split(':').every(value => Number(value) === 0))) {
      throw new Error('Aqua Duration must be a positive HH:MM value.');
    }

    if (hasEndTime && (typeof params.endTime !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(params.endTime))) {
      throw new Error('Aqua End Time must be a valid HH:MM value.');
    }
  }

  let duration: number | undefined;

  if ((order.params as AlgoParams).duration) {
    const time: string[] | undefined = String((order.params as AlgoParams).duration).split(':');
    let hours; let minutes;
    if (time) {
      ([hours, minutes] = time);
    }

    if (hours && minutes) {
      duration = Number(hours) * 60 * 60 + Number(minutes) * 60;
    }
  }

  const additionalParams = Object.keys((order.params as AlgoParams)).filter(key =>
    !['algoId', 'endTime', 'launchImmediately', 'startTime', 'quantity'].includes(key)
    && !isRunDerivedAlgoParameterId(key));
  let paramsToSubmit: AlgoParameterItem[] = [];
  const paramsToRemove: AlgoParameterItem[] = [];
  for (let i = 0; i < additionalParams.length; i++) {
    const key = additionalParams[i];
    const algoDef = getParameterDefinition(algo, key);
    if (!algoDef) {
      continue;
    }

    const value = isAqua ? normalizeAquaParameterValue(algoDef.algoParameterId, order.params[key]) : order.params[key];
    const paramValue = returnDataTypeValueField(algoDef, value);
    if (paramValue?.value === undefined) {
      continue;
    }

    if (algoDef.isOptional && (paramValue.value === '-' || paramValue.value === '')) {
      continue;
    } // A case exists where somehow 'dash' can come through for an optional parameter
    // because of how the frontend ui display the value. If this happens, its equivalent to undefined and can be omitted

    const preservesAteConstraintIdentity = isAteAlgo
      && (isPriceConstraintParameterId(key) || isNetTargetConstraintParameterId(key));
    paramsToSubmit.push({
      algoParameterId: preservesAteConstraintIdentity ? key : algoDef.algoParameterId,
      [paramValue.field]: paramValue.value,
    });
  }

  const aquaTimeInForce = Object.entries(order.params).find(([parameterId]) =>
    normalizeAquaParameterId(parameterId) === 'TIMEINFORCE')?.[1];
  if (isAqua
    && aquaTimeInForce !== undefined
    && !paramsToSubmit.some(parameter => normalizeAquaParameterId(parameter.algoParameterId) === 'TIMEINFORCE')) {
    paramsToSubmit.push({
      algoParameterId: 'TIME_IN_FORCE',
      intValue: 0,
    });
  }

  const orderTypeDefinition = algo.parameterDefinitions.find(definition => isOrderTypeDataType(definition.dataType));
  if (orderTypeDefinition && !paramsToSubmit.some(param => param.algoParameterId === orderTypeDefinition.algoParameterId)) {
    paramsToSubmit.push({
      algoParameterId: orderTypeDefinition.algoParameterId,
      stringValue: resolveDefaultOrderType(orderTypeDefinition.possibleSelectValues || []),
    });
  }

  const orderType = orderTypeDefinition ? paramsToSubmit.find(param => param.algoParameterId === orderTypeDefinition.algoParameterId)?.stringValue : undefined;

  appendFallbackLimitPriceParameter(paramsToSubmit, algo, orderType, order);
  if (isAqua && ['3', '4'].includes(getAquaOrderType(orderType) ?? '')) {
    const stopPrice = getNumberValue(order.params.stopPrice ?? order.params.STOP_PRICE);
    if (stopPrice !== undefined
      && !paramsToSubmit.some(parameter => normalizeAquaParameterId(parameter.algoParameterId) === 'STOPPRICE')) {
      paramsToSubmit.push({algoParameterId: 'STOP_PRICE', doubleValue: stopPrice});
    }
  }

  // New launches follow route-derived canonical semantics; modifies retain the persisted constraint identity.
  if (modifyingAlgo === undefined) {
    paramsToSubmit = canonicalizeAteConstraintParameter(paramsToSubmit, order, String(algo.algoType));
  } else if (isAteAlgo) {
    paramsToSubmit = normalizeAteModifyConstraintAliases(paramsToSubmit);
  }

  validateAlgoLimitPriceTick(paramsToSubmit, order);

  let startTime: Date | undefined = isAqua && isMissingParameterValue(order.params.startTime)
    ? undefined
    : new Date(toDateObject((order.params as AlgoParams).startTime?.toString() || ''));
  let endTime: Date | undefined = (order.params as AlgoParams).endTime ? new Date(toDateObject((order.params as AlgoParams).endTime)) : undefined;
  let launchImmediately: boolean = (order.params as AlgoParams).launchImmediately ? (order.params as AlgoParams).launchImmediately! : false;

  if (order.ticketType === OrderTypes.Spread && order.symbols) {
    paramsToSubmit.push({
      algoParameterId: MULTILEG_TARGET_IDENTITY_PARAMETER_ID,
      stringValue: `${order.symbols[0].segmentMic}::${order.symbols[0].symbol}`,
    });
  }

  // JPM provider orders send complete snapshots and must not use ATE delta lookups.
  if (modifyingAlgo?.algoRunId && !isJpmAlgoType(submittedAlgoType)) {
    const algoDetails: SingleAlgoFullReport = await getAlgoDetails(modifyingAlgo.algoRunId);
    const params = order.params as AlgoParams;
    const originalDuration = getAlgoDurationInSeconds(algoDetails);
    let submittedConstraint = isAteAlgo
      ? paramsToSubmit.find(parameter =>
        isPriceConstraintParameterId(parameter.algoParameterId)
        || isNetTargetConstraintParameterId(parameter.algoParameterId))
      : undefined;

    // If duration has changed, the endTime needs to be modified
    // startTime = moment.utc(algoDetails.startDate).toDate();
    if (duration === originalDuration && (!isAqua || !endTime)) {
      endTime = undefined;
    } else {
      const tuples = params.duration === undefined ? undefined : String(params.duration).split(':');
      if (tuples?.length && !endTime && !isAqua) {
        endTime = (moment.utc(startTime).add(tuples[0], 'hours').add(tuples[1], 'minutes')).toDate();
      }
    }

    const existingEntries = getAlgoDetailsParameterEntries(algoDetails);
    const existingConstraintIds = [...new Set(existingEntries
      .map(([parameterId]) => parameterId)
      .filter(parameterId => getAteConstraintKind(parameterId) !== undefined))];
    const existingConstraintId = existingConstraintIds.length === 1
      ? existingConstraintIds[0]
      : undefined;
    if (
      submittedConstraint !== undefined
      && existingConstraintId !== undefined
      && getAteConstraintKind(submittedConstraint.algoParameterId) === getAteConstraintKind(existingConstraintId)
    ) {
      const submittedConstraintIndex = paramsToSubmit.indexOf(submittedConstraint);
      submittedConstraint = {...submittedConstraint, algoParameterId: existingConstraintId};
      paramsToSubmit[submittedConstraintIndex] = submittedConstraint;
    }

    paramsToSubmit = paramsToSubmit.filter(parameter => hasSubmittedParameterChanged(parameter, existingEntries, algo));
    const conflictingExistingConstraints = submittedConstraint === undefined
      ? []
      : existingEntries.filter(([key]) => (
        isPriceConstraintParameterId(key) || isNetTargetConstraintParameterId(key)
      ) && normalizeConstraintParameterId(key) !== normalizeConstraintParameterId(submittedConstraint.algoParameterId));
    // QEP modifies carry the authoritative constraint once even when its value is unchanged.
    if (submittedConstraint && !paramsToSubmit.includes(submittedConstraint)) {
      paramsToSubmit.push(submittedConstraint);
    }

    existingEntries.forEach(([key]) => {
      if (conflictingExistingConstraints.some(([existingKey]) => existingKey === key)) {
        paramsToRemove.push({algoParameterId: key});
        return;
      }

      const draftParameter = findDraftParameterMatch(key, params, algo);
      if (draftParameter.found && isMissingParameterValue(draftParameter.value)) {
        paramsToRemove.push({
          algoParameterId: key,
        });
      }
    });

    const oneMinute = Duration.seconds(60);

    const originalStart = Time.fromUtcDateString(getAlgoStartDate(algoDetails));
    const startTimeParam = (order.params as AlgoParams).startTime;
    const draftStart = Time.fromHHMM(startTimeParam ? startTimeParam.toString() : '');
    if (originalStart.isValid() && draftStart.isValid() && Duration.between(originalStart, draftStart, false) < oneMinute) {
      startTime = undefined;
    }

    const originalEnd = Time.fromUtcDateString(getAlgoEndDate(algoDetails));
    const endTimeParam = (order.params as AlgoParams).endTime;
    const draftEnd = Time.fromHHMM(endTimeParam ? endTimeParam.toString() : '');
    if (originalEnd.isValid() && draftEnd.isValid() && Duration.between(originalEnd, draftEnd, false) < oneMinute) {
    //  endTime = undefined;
    }

    // Already-started display timing must not become a relaunch instruction or suppress parameter deltas.
    if (hasAlgoStarted(algoDetails)) {
      startTime = undefined;
      launchImmediately = false;
    }
  }

  const algoParams: RuntimeAlgoRunRequest['algoRunRequest'] = {
    algoType: submittedAlgoType,
    baseCurrencyCodeIso: 'USD',
    durationInSeconds: duration,
    launchImmediately,
    startTimeRequestedUtc: startTime!,
    endTimeRequestedUtc: endTime,

    algoTradeRequests: order.legs.map((leg: OrderRecord): RuntimeAlgoRunRequest['algoRunRequest']['algoTradeRequests'][number] => {
      const {symbol, account, direction, quantity} = leg;
      if (!symbol) {
        throw new Error('Missing symbol for algo request.');
      }

      if (!account) {
        throw new Error('Missing account for algo request.');
      }

      const tradeRequest: RuntimeAlgoRunRequest['algoRunRequest']['algoTradeRequests'][number] = {
        symbolIdentity: buildAlgoTradeSymbolIdentity(leg, symbol),
        targetQuantity: Number(quantity),
        side:
          direction === Direction.Buy
            ? OrderSide.ORDERSIDE_BUY
            : OrderSide.ORDERSIDE_SELL,
        accountInfo: {
          internalAccountCode: account.accountId,
          externalAccountCode: account.accountId,
          memo: account.memo ?? null,
          execCode: account.execCode,
          executionTag: account.execCode,
          customText1: account.notes[0] ?? null,
          customText2: account.notes[1] ?? null,
          customText3: account.notes[2] ?? null,
          neovestAccountCode: account.accountId,
          deliverToCompId: account.execCode ?? '',
        },
      };
      return tradeRequest;
    }),
    additionalParameters: paramsToSubmit,
  };

  if (paramsToRemove.length > 0) {
    algoParams.removeParameters = paramsToRemove;
  }

  const accountInfo = algoParams.algoTradeRequests[0]?.accountInfo;
  if (!accountInfo) {
    throw new Error('Missing account for algo request.');
  }

  const algoRequest: RuntimeAlgoRunRequest = {
    algoRunRequest: algoParams,
    // **NOTE** ATE will fail without parent account info. Since every leg has an account, it will be ignored
    // for now, just yoink it out of the top leg
    accountInfo,
  };

  return algoRequest;
}

function toDateObject(time?: string) {
  if (!time) {
    return new Date();
  }

  const [hours, minutes] = time.split(':');

  const date = new Date();

  date.setHours(Number(hours));
  date.setMinutes(Number(minutes));
  date.setSeconds(0);
  date.setMilliseconds(0);

  return date;
}

export const isModifyAlgoValid = (order: OrderEntryOrder, algo?: SingleAlgoFullReport): boolean => {
  if (!algo) {
    return false;
  }

  try {
    const params = order.params as AlgoParams;

    const enteredDuration = typeof params.duration === 'number'
      ? params.duration * 60
      : convertHHMMToSeconds(params.duration ?? '0');

    if (params?.startTime != undefined && params?.endTime != undefined && params.startTime >= params.endTime) {
      return false;
    }

    const algoDuration = getAlgoDurationInSeconds(algo);
    if (algoDuration && enteredDuration && enteredDuration !== algoDuration) {
      return true;
    }

    const algoStartDate = getAlgoStartDate(algo);
    if (algoStartDate && params.startTime) {
      const startHour = moment.utc(algoStartDate).local().hours();
      const startMinute = moment.utc(algoStartDate).local().minutes();
      const [paramsStartHour, paramsStartMinute] = params.startTime.toString().split(':').map(Number);// I hope this always works...

      if (startHour !== paramsStartHour || startMinute !== paramsStartMinute) {
        return true;
      }
    }

    const existingEntries = getAlgoDetailsParameterEntries(algo);
    if (existingEntries.length > 0) {
      const modifyLimitPrice = Object.keys(params).find(key => isLimitPriceParameterId(key));
      if (modifyLimitPrice) {
        // adding or modifying params
        const currentAlgoLimitPrice = existingEntries.find(([key]) => isLimitPriceParameterId(key));
        if (currentAlgoLimitPrice) {
          // modifying limit price
          const price = params[modifyLimitPrice];
          const limitPrice = getLimitComparisonValue(price);
          const startLimitPrice = getLimitComparisonValue(currentAlgoLimitPrice[1]);
          if (limitPrice !== startLimitPrice) {
            return true;
          }
        }

        // adding limit price
        return true;
      }

      // removing limit price
      return true;
    }
  } catch (error) {
    console.error('Error determining if modified algo is valid:', error);
  }

  return false;
};
